/**
 * 보고서 공통 쉘 컴포넌트
 * StickyHeader(네비게이션/제목) + 하단 고정 바(액션) + 에러/정보 배너 + 로딩/에러 상태 + 콘텐츠 + 모달 슬롯
 * 작성 페이지, 상세 페이지 모두에서 사용.
 *
 * 2026-07-26 web → rc 승격(시뮬레이션 chrome 배포판 동일화) — rc는 router 무의존이라
 * 라우팅(backLink·fatalError 돌아가기 링크)은 host 주입: onNavigateBack + renderLink
 * (web 재수출층이 useNavigate/react-router Link로 공급, admin 시뮬은 onBack만 사용).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { buttonClassName } from '@safetyhub/ui';
import { StickyHeader, type StickyHeaderAction } from './StickyHeader';
import { Tooltip } from './Tooltip';
// 하단 카드 결 정본 — ComposeWritePageShell과 공유(시안 작성·상세류 푸터 공통 골격).
import { FOOTER_CARD_STYLE } from './ComposeWritePageShell';

/**
 * 하단 고정 바에서 menuItems를 가진 액션을 위로 열리는 드롭다운으로 렌더.
 * 하단바이므로 메뉴는 버튼 위쪽(bottom-full)으로 펼쳐지며, 바깥 클릭/Esc로 닫힘.
 */
function FooterMenuButton({ action, className }: { action: StickyHeaderAction; className: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const items = action.menuItems ?? [];

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {action.label}
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 bottom-full mb-2 w-44 bg-white rounded-card shadow-lg border z-[70] py-1"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              title={item.tooltip}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={`w-full px-4 py-2.5 text-sm font-medium text-left transition-colors ${
                item.disabled
                  ? 'opacity-50 cursor-not-allowed text-gray-400'
                  : item.variant === 'danger'
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface ReportWriteShellProps {
  // Header
  backLink?: string;
  /** backLink 클릭 시 라우팅(host 주입 — web 재수출층이 useNavigate(replace)로 공급) */
  onNavigateBack?: (to: string) => void;
  /**
   * fatalError '돌아가기' 링크 렌더(host 주입 — web 재수출층이 react-router Link로 공급).
   * 미주입 시 일반 anchor 폴백.
   */
  renderLink?: (args: { to: string; className: string; children: ReactNode }) => ReactNode;
  /** StickyHeader sticky top 오프셋(px) — 호스트 상단 고정 요소(시뮬 배너 등) 아래 고정용 */
  stickyTopOffset?: number;
  onBack?: () => void;
  backLabel?: string;
  title?: string;
  subtitle?: string;
  /** StickyHeader에 표시할 액션 (네비게이션 용도로만 사용 권장) */
  headerActions?: StickyHeaderAction[];
  /** StickyHeader 왼쪽 영역(뒤로가기/제목 옆) 액션 */
  headerLeftActions?: StickyHeaderAction[];
  /** StickyHeader 왼쪽 영역 커스텀 콘텐츠 (상태 배지 등, leftActions 앞) */
  headerLeftContent?: React.ReactNode;
  /** StickyHeader의 ref (높이 측정 등에 사용) */
  stickyHeaderRef?: React.Ref<HTMLDivElement>;
  /** 모바일(phone)에서 헤더 우측 actions 전부를 ⋯ 더보기 메뉴로 collapse (기본 false) */
  collapseHeaderActionsOnMobile?: boolean;

  // Footer (하단 고정 바)
  footerActions?: StickyHeaderAction[];
  footerLeft?: React.ReactNode;

  // Banners
  error?: string;
  onDismissError?: () => void;
  infoBanner?: React.ReactNode;

  // Content
  children: React.ReactNode;

  // Modals
  modals?: React.ReactNode;

  // Loading
  isLoading?: boolean;
  loadingMessage?: string;

  // Fatal Error
  fatalError?: string;

  // Layout
  maxWidth?: string;
}

export function ReportWriteShell({
  backLink,
  onNavigateBack,
  renderLink,
  stickyTopOffset,
  onBack,
  backLabel,
  title,
  subtitle,
  headerActions,
  headerLeftActions,
  headerLeftContent,
  stickyHeaderRef,
  collapseHeaderActionsOnMobile = false,
  footerActions,
  footerLeft,
  error,
  onDismissError,
  infoBanner,
  children,
  modals,
  isLoading,
  loadingMessage,
  fatalError,
  maxWidth = 'max-w-4xl xl:max-w-6xl',
}: ReportWriteShellProps) {
  if (isLoading) {
    return (
      <div className={`${maxWidth} mx-auto px-4 py-6`}>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mb-4" />
          <p className="text-gray-500">{loadingMessage || '불러오는 중...'}</p>
        </div>
      </div>
    );
  }

  if (fatalError) {
    return (
      <div className={`${maxWidth} mx-auto px-4 py-6`}>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">오류 발생</h3>
          <p className="text-gray-500 mb-4">{fatalError}</p>
          {renderLink ? (
            renderLink({
              to: backLink || '/',
              className:
                'inline-flex items-center px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-control hover:bg-brand-100',
              children: '돌아가기',
            })
          ) : (
            <a
              href={backLink || '/'}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-control hover:bg-brand-100"
            >
              돌아가기
            </a>
          )}
        </div>
      </div>
    );
  }

  // headerActions가 없고 footerActions가 있으면 → 하단 고정 패턴
  const useFooter = footerActions && footerActions.length > 0;

  return (
    // 하단 여백: 떠 있는 카드(높이 ~70px + bottom 16px)가 콘텐츠 끝을 가리지 않게 7rem.
    <div style={useFooter ? { paddingBottom: 'calc(var(--bottom-nav-height, 0px) + 7rem)' } : undefined}>
      <StickyHeader
        ref={stickyHeaderRef}
        backLink={backLink}
        onNavigateBack={onNavigateBack}
        stickyTopOffset={stickyTopOffset}
        onBack={onBack}
        backLabel={backLabel}
        title={title}
        subtitle={subtitle}
        actions={headerActions ?? []}
        leftActions={headerLeftActions ?? []}
        leftContent={headerLeftContent}
        maxWidth={maxWidth}
        collapseActionsOnMobile={collapseHeaderActionsOnMobile}
      />

      {/* 에러 배너 */}
      {error && (
        <div className={`${maxWidth} mx-auto px-4 mt-4`}>
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="ml-3 text-sm text-red-700">{error}</p>
              {onDismissError && (
                <button onClick={onDismissError} className="ml-auto text-red-400 hover:text-red-500">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 정보 배너 */}
      {infoBanner && (
        <div className={`${maxWidth} mx-auto px-4 mt-4`}>
          {/* 상태 배너가 아니라 작성 안내 문구 — 상태색 blue가 아니라 브랜드 톤을 쓴다
              (web LawSearchLoadingIndicator와 같은 판단). */}
          <div className="p-3 bg-brand-50 border border-brand-100 rounded-card">
            <p className="text-xs sm:text-sm text-brand-700">{infoBanner}</p>
          </div>
        </div>
      )}

      {/* 콘텐츠 */}
      {children}

      {/* 하단 고정 바 — 시안의 모든 작성·상세류 푸터 공통 골격(떠 있는 카드, .ir-footer/.tbm-footer
          동형). 결은 ComposeWritePageShell의 FOOTER_CARD_STYLE 정본 공유 — 전폭 바를 유지하면
          composed 작성 페이지(카드)와 이 셸의 소비처(상세·구 작성·TBM)가 갈린다(2026-08-02 지적).
          바깥 fixed 래퍼는 투명·전폭(고정 축), 카드가 콘텐츠 컬럼 폭 안에 뜬다.

          ⚠️ 가로 축은 호스트가 소유한다 — `left`는 **호스트 콘텐츠 컬럼의 좌측 오프셋**이고
          `--bottom-nav-height`(세로)와 같은 축이다. `fixed`는 뷰포트 기준이라 호스트의 사이드바
          들여쓰기를 스스로 알지 못하고, 그러면 안쪽 `mx-auto`가 **뷰포트** 중앙에 정렬돼
          그 폭의 절반만큼 왼쪽으로 쏠린다. web `Layout.tsx`가 그 변수를 설정하고,
          **기본값 0이라 Layout 밖(외부 결재·인쇄)과 admin은 영향이 없다**.
          ⚠️ **이 정렬을 재는 자동 검사는 없다**(레이아웃은 jsdom이 못 잰다 — `getBoundingClientRect()`가
          0을 준다) — 바꾸면 **전후 캡처**로 확인할 것. 2026-10-13에 이 자리가 `left: 0`이라
          **128px 왼쪽으로 쏠려** 있었고 사용자가 화면에서 발견했다(사이드바 256 ÷ 2). */}
      {useFooter && (
        <div
          className="fixed z-40"
          style={{
            left: 'var(--app-content-left, 0px)',
            right: 0,
            bottom: 'calc(var(--bottom-nav-height, 0px) + 16px)',
          }}
        >
          <div className={`${maxWidth} mx-auto px-3 sm:px-4`}>
            <div style={FOOTER_CARD_STYLE}>
              {footerLeft && (
                <div className="flex min-w-0 items-center gap-2">
                  {footerLeft}
                </div>
              )}
              {/* 좌측(footerLeft) 유무와 무관하게 버튼은 우측 — 시안 space-between 동형 */}
              <div className="ml-auto flex items-center justify-end gap-2">
              {footerActions.map((action) => {
                const wrapperCls = action.hiddenOnMobile ? 'hidden sm:block' : undefined;
                // 시각 출처는 packages/ui `Button` 단일 — StickyHeader와 같은 이유로 수렴.
                // 반응형 패딩을 버리고 md 고정: 이 푸터 액션 바는 `justify-end` 한 줄이고,
                // 폰에서 빠질 것은 이미 `hiddenOnMobile`(wrapperCls)로 각 액션이 개별 제어한다.
                // 가로는 md(px-3.5)가 기존 데스크탑(px-4)보다 좁아 넘칠 여지가 줄어든다.
                const inactive = action.disabled || action.loading;
                const cls = buttonClassName(
                  action.variant ?? 'secondary',
                  'md',
                  [
                    'whitespace-nowrap',
                    inactive ? 'opacity-50 cursor-not-allowed' : '',
                    action.hasChanges && action.variant === 'primary' && !inactive
                      ? 'ring-2 ring-brand-300 ring-offset-1'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' '),
                );
                // menuItems가 있으면 드롭다운 트리거로 렌더
                if (action.menuItems) {
                  return (
                    <span key={action.key} className={wrapperCls}>
                      <FooterMenuButton action={action} className={cls} />
                    </span>
                  );
                }
                const displayLabel = action.mobileLabel
                  ? <><span className="sm:hidden">{action.mobileLabel}</span><span className="hidden sm:inline">{action.label}</span></>
                  : action.label;
                const btnEl = (
                  <button
                    type="button"
                    onClick={action.onClick}
                    disabled={action.disabled || action.loading}
                    className={cls}
                  >
                    {action.loading ? (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : action.icon}
                    {displayLabel}
                    {action.hasChanges && !action.loading && (
                      <span className="w-2 h-2 bg-orange-500 rounded-full" />
                    )}
                  </button>
                );

                if (action.disabled && action.tooltip) {
                  return (
                    <Tooltip key={action.key} content={action.tooltip} placement="top" align="right">
                      <div className={`cursor-help${wrapperCls ? ` ${wrapperCls}` : ''}`}>
                        <div className="pointer-events-none">{btnEl}</div>
                      </div>
                    </Tooltip>
                  );
                }

                return <span key={action.key} className={wrapperCls}>{btnEl}</span>;
              })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 모달 슬롯 */}
      {modals}
    </div>
  );
}
